import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../../config/api';
import confetti from 'canvas-confetti';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ListAndTagDropdown from '../../shared/ListAndTagDropdown';
import GameLayout from '../../shared/GameLayout';
import Breadcrumb from '../../shared/Breadcrumb';

const DefinitionGame = () => {
  const [selectedList, setSelectedList] = useState('');
  const [difficulty, setDifficulty] = useState('easy');
  const [duration, setDuration] = useState('short');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [gameData, setGameData] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswerShown, setIsAnswerShown] = useState(false);
  const [isGameFinished, setIsGameFinished] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [showError, setShowError] = useState(false);
  const [letterInputs, setLetterInputs] = useState([]);
  const inputRefs = React.useRef([]);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [selectedTag, setSelectedTag] = useState('');

  useEffect(() => {
    if (gameData && difficulty === 'hard') {
      const currentWord = gameData.definicje[currentIndex]?.odpowiedz || '';
      setLetterInputs(currentWord.split('').map(char => char === ' ' ? ' ' : ''));
      inputRefs.current = inputRefs.current.slice(0, currentWord.length);
    }
  }, [currentIndex, gameData, difficulty]);

  const triggerConfetti = () => {
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  };

  const handleStartGame = async () => {
    setIsLoading(true);
    setError('');
    setCurrentIndex(0);
    setScore(0);
    setSelectedAnswer(null);
    setIsAnswerShown(false);
    setIsGameFinished(false);

    try {
      const data = await api.generateDefinitionGame(
        selectedList,
        getWordCount(),
        selectedTag 
      );
      
      setGameData(data);
    } catch (err) {
      setError('Wystąpił błąd podczas rozpoczynania gry');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnswerSelect = (answer) => {
    if (isAnswerShown) return;
    
    const currentQuestion = gameData.definicje[currentIndex];
    const isCorrect = answer === currentQuestion.odpowiedz;
    
    setSelectedAnswer(answer);
    setIsAnswerShown(true);
    
    if (isCorrect) {
      setScore(prev => prev + 1);
    }
  };

  const handleLetterInput = (index, value) => {
    if (isAnswerShown) return;

    const newValue = value.slice(-1); // Bierz tylko ostatnią literę
    const newInputs = [...letterInputs];
    newInputs[index] = newValue;
    setLetterInputs(newInputs);

    // Przejdź do następnego inputa
    if (newValue && index < letterInputs.length - 1) {
      const nextNonSpaceIndex = findNextInputIndex(index);
      if (nextNonSpaceIndex !== -1) {
        inputRefs.current[nextNonSpaceIndex]?.focus();
      }
    }

    // Aktualizuj pełną odpowiedź
    setUserInput(newInputs.join(''));
  };

  const handleKeyDown = (index, e) => {
    if (isAnswerShown) return;

    if (e.key === 'Backspace' && !letterInputs[index] && index > 0) {
      // Przejdź do poprzedniego inputa przy backspace
      const prevNonSpaceIndex = findPrevInputIndex(index);
      if (prevNonSpaceIndex !== -1) {
        e.preventDefault();
        inputRefs.current[prevNonSpaceIndex]?.focus();
      }
    }
  };

  const findNextInputIndex = (currentIndex) => {
    for (let i = currentIndex + 1; i < letterInputs.length; i++) {
      if (letterInputs[i] !== ' ') return i;
    }
    return -1;
  };

  const findPrevInputIndex = (currentIndex) => {
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (letterInputs[i] !== ' ') return i;
    }
    return -1;
  };

  const handleSubmitAnswer = () => {
    if (isAnswerShown || !userInput) return;
    
    const currentQuestion = gameData.definicje[currentIndex];
    const isCorrect = userInput.toLowerCase() === currentQuestion.odpowiedz.toLowerCase();
    
    setSelectedAnswer(userInput);
    setIsAnswerShown(true);
    
    if (isCorrect) {
      setScore(prev => prev + 1);
    } else {
      setShowError(true);
      setTimeout(() => setShowError(false), 2000);
    }
  };

  const handleNextQuestion = () => {
    if (!isAnswerShown) return;
    
    if (currentIndex < gameData.definicje.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setIsAnswerShown(false);
      setHintsUsed(0);
    } else {
      setIsGameFinished(true);
      const finalScore = score + (selectedAnswer === gameData.definicje[currentIndex].odpowiedz ? 1 : 0);
      if (finalScore >= gameData.definicje.length - 1) {
        triggerConfetti();
      }
    }
  };

  const getWordCount = () => {
    switch (duration) {
      case 'short': return 3;
      case 'medium': return 6;
      case 'long': return 10;
      default: return 3;
    }
  };

  const handleHint = () => {
    if (isAnswerShown) return;

    const currentWord = gameData.definicje[currentIndex].odpowiedz;
    const emptyIndexes = [];
    
    // Znajdź wszystkie puste pola (nie spacje)
    letterInputs.forEach((letter, index) => {
      if (!letter && currentWord[index] !== ' ') {
        emptyIndexes.push(index);
      }
    });

    if (emptyIndexes.length > 0) {
      // Wybierz losowy indeks z pustych pól
      const randomIndex = emptyIndexes[Math.floor(Math.random() * emptyIndexes.length)];
      const newInputs = [...letterInputs];
      newInputs[randomIndex] = currentWord[randomIndex];
      setLetterInputs(newInputs);
      setUserInput(newInputs.join(''));
      setHintsUsed(prev => prev + 1);
    }
  };

  const renderBreadcrumb = () => (
    <Breadcrumb
      items={[
        { label: 'Nauka', path: '/study' },
        { label: 'Zgadnij słowo z definicji' }
      ]}
    />
  );

  const renderLoadingState = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="text-center space-y-4"
    >
      <div className="w-16 h-16 mx-auto border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-lg text-neutral-600">Przygotowujemy Twoją grę...</p>
      <p className="text-sm text-neutral-500">To może chwilę zająć, ale będzie warto!</p>
    </motion.div>
  );

  const renderAnswerPlaceholder = (word) => {
    const words = word.split(' ');
    return (
      <div className="flex items-center justify-center gap-4">
        {words.map((word, wordIndex) => (
          <div key={wordIndex} className="flex items-center gap-1">
            {word.split('').map((_, letterIndex) => (
              <div
                key={letterIndex}
                className="w-6 h-0.5 bg-neutral-400"
              />
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderAnswerInputs = (word) => {
    return (
      <div className="flex items-center justify-center gap-4">
        {word.split('').map((char, index) => (
          <React.Fragment key={index}>
            {char === ' ' ? (
              <div className="w-4" />
            ) : (
              <input
                ref={el => inputRefs.current[index] = el}
                type="text"
                maxLength={1}
                value={letterInputs[index] || ''}
                onChange={(e) => handleLetterInput(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={isAnswerShown}
                className={`w-8 h-10 text-center border-b-2 focus:border-primary-500 outline-none text-lg font-medium
                  ${isAnswerShown 
                    ? letterInputs[index]?.toLowerCase() === word[index]?.toLowerCase()
                      ? 'border-green-500 text-green-700'
                      : 'border-red-500 text-red-700'
                    : showError
                    ? 'border-red-500'
                    : 'border-neutral-400'
                  }
                  ${isAnswerShown ? 'bg-transparent' : 'hover:border-neutral-600'}
                  transition-colors`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderGameContent = () => {
    if (!gameData || !gameData.definicje[currentIndex]) return null;

    const currentQuestion = gameData.definicje[currentIndex];
    const isLastQuestion = currentIndex === gameData.definicje.length - 1;

    return (
      <motion.div
        key={currentIndex}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="space-y-6"
      >
        <div className="text-center">
          <div className="mb-4">
            <span className="text-sm text-neutral-500">
              Pytanie {currentIndex + 1} z {gameData.definicje.length}
            </span>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xl font-medium mb-8"
          >
            {currentQuestion.definicja}
          </motion.p>
          
          {difficulty === 'easy' ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-2 gap-3"
            >
              {currentQuestion.lista_slowek.map((word, index) => (
                <motion.button
                  key={index}
                  onClick={() => handleAnswerSelect(word)}
                  disabled={isAnswerShown}
                  whileHover={!isAnswerShown ? { scale: 1.02 } : {}}
                  whileTap={!isAnswerShown ? { scale: 0.98 } : {}}
                  className={`p-4 rounded-lg border transition-colors ${
                    !isAnswerShown
                      ? 'border-neutral-200 hover:border-primary-500 hover:bg-primary-50'
                      : word === currentQuestion.odpowiedz
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : word === selectedAnswer && word !== currentQuestion.odpowiedz
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-neutral-200'
                  }`}
                >
                  <span className="relative select-none">
                    {word}
                    {isAnswerShown && word === currentQuestion.odpowiedz && (
                      <motion.svg
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="absolute -right-6 top-1/2 -translate-y-1/2 w-4 h-4 text-green-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </motion.svg>
                    )}
                  </span>
                </motion.button>
              ))}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="space-y-6"
            >
              {renderAnswerInputs(currentQuestion.odpowiedz)}
              <div className="flex items-center justify-center gap-4">
                {!isAnswerShown && (
                  <>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleHint}
                      className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 flex items-center gap-2"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                        />
                      </svg>
                      Podpowiedź
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleSubmitAnswer}
                      className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    >
                      Sprawdź
                    </motion.button>
                  </>
                )}
              </div>
              {isAnswerShown && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`text-lg font-medium ${
                    selectedAnswer.toLowerCase() === currentQuestion.odpowiedz.toLowerCase()
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}
                >
                  {selectedAnswer.toLowerCase() === currentQuestion.odpowiedz.toLowerCase() ? (
                    <>
                      Poprawna odpowiedź!
                      {hintsUsed > 0 && (
                        <span className="text-sm text-neutral-500 block">
                          (Użyto {hintsUsed} {hintsUsed === 1 ? 'podpowiedzi' : 'podpowiedzi'})
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      Prawidłowa odpowiedź to: <span className="font-bold">{currentQuestion.odpowiedz}</span>
                    </>
                  )}
                </motion.div>
              )}
            </motion.div>
          )}

          <div className="h-16 flex items-center justify-center">
            <AnimatePresence>
              {isAnswerShown && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-6"
                >
                  <motion.button
                    onClick={handleNextQuestion}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  >
                    {isLastQuestion ? 'Zakończ grę' : 'Następne pytanie'}
                    <svg
                      className="ml-2 w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderGameSummary = () => {
    if (!isGameFinished) return null;

    const finalScore = score + (selectedAnswer === gameData.definicje[currentIndex].odpowiedz ? 1 : 0);
    const isVictory = finalScore >= gameData.definicje.length - 1;

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center space-y-4"
      >
        <h2 className="text-2xl font-semibold">
          {isVictory ? 'Gratulacje! 🎉' : 'Koniec gry!'}
        </h2>
        <p className="text-xl">
          Twój wynik: {finalScore} z {gameData.definicje.length}
        </p>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setGameData(null)}
          className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          Zagraj ponownie
        </motion.button>
      </motion.div>
    );
  };

  return (
    <GameLayout
      breadcrumbItems={[
        { label: 'Nauka', path: '/study' },
        { label: 'Zgadnij słowo z definicji' }
      ]}
    >
      {!gameData ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {isLoading ? (
            <AnimatePresence mode="wait">
              {renderLoadingState()}
            </AnimatePresence>
          ) : (
            <>
              <ListAndTagDropdown
                selectedList={selectedList}
                selectedTag={selectedTag}
                onListChange={setSelectedList}
                onTagChange={setSelectedTag}
              />

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Poziom trudności
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setDifficulty('easy')}
                    className={`p-4 rounded-lg border transition-colors ${
                      difficulty === 'easy'
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-neutral-200 hover:border-primary-500'
                    }`}
                  >
                    <h3 className="text-lg font-medium mb-2">Łatwy</h3>
                    <p className="text-sm text-neutral-600">
                      Wybierz poprawne słowo spośród czterech opcji
                    </p>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setDifficulty('hard')}
                    className={`p-4 rounded-lg border transition-colors ${
                      difficulty === 'hard'
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-neutral-200 hover:border-primary-500'
                    }`}
                  >
                    <h3 className="text-lg font-medium mb-2">Trudny</h3>
                    <p className="text-sm text-neutral-600">
                      Wpisz poprawne słowo
                    </p>
                  </motion.button>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Długość rozgrywki
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setDuration('short')}
                    className={`p-4 rounded-lg border transition-colors ${
                      duration === 'short'
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-neutral-200 hover:border-primary-500'
                    }`}
                  >
                    <h3 className="text-lg font-medium mb-2">Krótka</h3>
                    <p className="text-sm text-neutral-600">3 słówka</p>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setDuration('medium')}
                    className={`p-4 rounded-lg border transition-colors ${
                      duration === 'medium'
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-neutral-200 hover:border-primary-500'
                    }`}
                  >
                    <h3 className="text-lg font-medium mb-2">Średnia</h3>
                    <p className="text-sm text-neutral-600">6 słówek</p>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setDuration('long')}
                    className={`p-4 rounded-lg border transition-colors ${
                      duration === 'long'
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-neutral-200 hover:border-primary-500'
                    }`}
                  >
                    <h3 className="text-lg font-medium mb-2">Długa</h3>
                    <p className="text-sm text-neutral-600">10 słówek</p>
                  </motion.button>
                </div>
              </motion.div>

              {error && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-red-500 text-sm mt-2"
                >
                  {error}
                </motion.div>
              )}

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleStartGame}
                disabled={isLoading}
                className="w-full py-1 px-4 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Ładowanie...' : 'Rozpocznij grę'}
              </motion.button>
            </>
          )}
        </motion.div>
      ) : (
        <AnimatePresence mode="wait">
          {!isGameFinished && renderGameContent()}
          {renderGameSummary()}
        </AnimatePresence>
      )}
    </GameLayout>
  );
};

export default DefinitionGame; 